import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PageGen,
} from '../../pdf/pdf';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import { Random } from '@educraft/base';
import {
  generateSudoku,
  drawSudokuGrid,
  ESudokuLevel,
  SudokuBoard,
  SudokuSize,
} from '../../pdf/board/sudoku';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);
const PAGE_W = PdfSize.A4.width;
const PAGE_H = PdfSize.A4.height;

const LEVEL_LABELS: Record<ESudokuLevel, string> = {
  [ESudokuLevel.Easy]: 'Easy',
  [ESudokuLevel.Medium]: 'Medium',
  [ESudokuLevel.Hard]: 'Hard',
  [ESudokuLevel.Expert]: 'Expert',
};

// ── Cell / font sizes by grid size ────────────────────────────────────────────

interface SizeConfig {
  cellPt: number;
  fontPt: number;
}

function sizeConfig(size: SudokuSize): SizeConfig {
  switch (size) {
    case 4:
      return { cellPt: MM(13), fontPt: 16 };
    case 9:
      return { cellPt: MM(8.5), fontPt: 11 };
    case 16:
      return { cellPt: MM(4.8), fontPt: 7 };
  }
}

// ── Shared drawing helpers ────────────────────────────────────────────────────

function drawPageTitle(page: PageGen, title: string): void {
  const font = new PdfFont(PdfFont.FONT_NAME, 11);
  page.drawString(
    title,
    font,
    PdfBrush.Black,
    null,
    new PdfRect(MARGIN, MM(5), PAGE_W - 2 * MARGIN, MM(8)),
    ETextAlign.CenterLeft
  );
}

function drawGridLabel(page: PageGen, label: string, x: number, y: number, w: number): void {
  const font = new PdfFont(PdfFont.FONT_NAME, 8);
  page.drawString(
    label,
    font,
    PdfBrush.Black,
    null,
    new PdfRect(x, y, w, MM(5)),
    ETextAlign.Center
  );
}

/**
 * Draws four grids in a 2×2 arrangement, centred on the page.
 * Column gap is fixed at MM(12); rows are spaced to use the available height evenly.
 */
function draw2x2(
  page: PageGen,
  boards: SudokuBoard[],
  cellPt: number,
  givenFont: PdfFont,
  solutionFont: PdfFont,
  showSolution: boolean,
  labels: string[]
): void {
  const gridW = boards[0].size * cellPt;
  const colGap = MM(12);
  const labelH = MM(6);
  const rowGap = MM(14);

  // Horizontal: centre both grids on the page
  const pairW = 2 * gridW + colGap;
  const startX = (PAGE_W - pairW) / 2;

  // Vertical: two rows below the title area (MM(16) top)
  const gridH = gridW; // square
  const startY = MM(16) + labelH;

  const xs = [startX, startX + gridW + colGap];
  const ys = [startY, startY + gridH + rowGap + labelH];

  for (let i = 0; i < 4; i++) {
    const x = xs[i % 2];
    const y = ys[Math.floor(i / 2)];
    drawGridLabel(page, labels[i], x, y - labelH, gridW);
    drawSudokuGrid(page, boards[i], x, y, cellPt, givenFont, solutionFont, showSolution);
  }
}

/**
 * Draws one grid centred on the page (used for 16×16).
 */
function draw1x1(
  page: PageGen,
  board: SudokuBoard,
  cellPt: number,
  givenFont: PdfFont,
  solutionFont: PdfFont,
  showSolution: boolean,
  label: string
): void {
  const gridW = board.size * cellPt;
  const labelH = MM(6);
  const x = (PAGE_W - gridW) / 2;
  const y = MM(16) + labelH;

  drawGridLabel(page, label, x, y - labelH, gridW);
  drawSudokuGrid(page, board, x, y, cellPt, givenFont, solutionFont, showSolution);
}

// ── Abstract base ─────────────────────────────────────────────────────────────

export abstract class SudokuBase implements ILayoutBaseGen {
  constructor(
    private readonly sudokuSize: SudokuSize,
    private readonly level: ESudokuLevel
  ) {}

  async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    const random = opts.data?.random ?? new Random();
    const { cellPt, fontPt } = sizeConfig(this.sudokuSize);
    const givenFont = new PdfFont(PdfFont.FONT_NAME, fontPt);
    const solutionFont = new PdfFont(PdfFont.FONT_NAME, fontPt);
    const levelLabel = LEVEL_LABELS[this.level];
    const sizeLabel = `${this.sudokuSize}×${this.sudokuSize}`;
    const is16x16 = this.sudokuSize === 16;
    const count = is16x16 ? 1 : 4;

    // Generate the required boards in sequence from the provided random source
    const boards: SudokuBoard[] = [];
    for (let i = 0; i < count; i++) {
      boards.push(generateSudoku(random, this.sudokuSize, this.level));
    }

    const page1 = pdf.addPage();
    const page2 = pdf.addPage();

    drawPageTitle(page1, `Sudoku ${sizeLabel} — ${levelLabel}`);
    drawPageTitle(page2, `Sudoku ${sizeLabel} — ${levelLabel} — Solution`);

    if (is16x16) {
      const board = boards[0];
      const puzzleLabel = `Puzzle  (${board.clueCount} clues)`;
      const solutionLabel = 'Solution';
      draw1x1(page1, board, cellPt, givenFont, solutionFont, false, puzzleLabel);
      draw1x1(page2, board, cellPt, givenFont, solutionFont, true, solutionLabel);
    } else {
      const puzzleLabels = boards.map((b, i) => `#${i + 1}  (${b.clueCount} clues)`);
      const solutionLabels = boards.map((_, i) => `#${i + 1}`);
      draw2x2(page1, boards, cellPt, givenFont, solutionFont, false, puzzleLabels);
      draw2x2(page2, boards, cellPt, givenFont, solutionFont, true, solutionLabels);
    }
  }
}

// ── 4×4 ───────────────────────────────────────────────────────────────────────

export class Sudoku__4x4__Easy extends SudokuBase {
  constructor() {
    super(4, ESudokuLevel.Easy);
  }
}
export class Sudoku__4x4__Medium extends SudokuBase {
  constructor() {
    super(4, ESudokuLevel.Medium);
  }
}
export class Sudoku__4x4__Hard extends SudokuBase {
  constructor() {
    super(4, ESudokuLevel.Hard);
  }
}
export class Sudoku__4x4__Expert extends SudokuBase {
  constructor() {
    super(4, ESudokuLevel.Expert);
  }
}

// ── 9×9 ───────────────────────────────────────────────────────────────────────

export class Sudoku__9x9__Easy extends SudokuBase {
  constructor() {
    super(9, ESudokuLevel.Easy);
  }
}
export class Sudoku__9x9__Medium extends SudokuBase {
  constructor() {
    super(9, ESudokuLevel.Medium);
  }
}
export class Sudoku__9x9__Hard extends SudokuBase {
  constructor() {
    super(9, ESudokuLevel.Hard);
  }
}
export class Sudoku__9x9__Expert extends SudokuBase {
  constructor() {
    super(9, ESudokuLevel.Expert);
  }
}

// ── 16×16 ────────────────────────────────────────────────────────────────────

export class Sudoku__16x16__Easy extends SudokuBase {
  constructor() {
    super(16, ESudokuLevel.Easy);
  }
}
export class Sudoku__16x16__Medium extends SudokuBase {
  constructor() {
    super(16, ESudokuLevel.Medium);
  }
}
export class Sudoku__16x16__Hard extends SudokuBase {
  constructor() {
    super(16, ESudokuLevel.Hard);
  }
}
